import React, { FC, useEffect, useRef, useState } from "react";
import { useMutation } from "react-query";
import { ReviewSummaryPayload, ReviewSummaryProps } from "./types";
import { ReviewSummaryRaw } from "./ReviewSummaryRaw";
import { getPudoMethodName } from "./transformers/pudoMethod";
import useCurrencyConfig from "./hooks/useCurrencyConfig";
import { useSettings, withSettingsProvider } from "@narvar/nth-hook-settings";
import {
  useHipaaSubmitReturnCartMutation,
  useSubmitReturnCartMutation,
  CommonEvent,
  useReturns,
  useReturnsConfig,
  useLanguage,
} from "@narvar/nth-kit-returns-headless";
import { setPudoOption } from "./utils/rootElmHelpers";

const ReviewSummaryC: FC<ReviewSummaryProps> = ({
  onSubmit,
  onBack,
  progressiveStep,
  activeStep,
  allowRetry,
}) => {
  const [policyChecked, setPolicyChecked] = useState(false);
  const [mailTo, setMailTo] = useState("");

  const {
    orderData,
    selectedItems,
    selectedPudoOption,
    client,
    setReturnCart,
    returnCart,
    userRole,
  } = useReturns();
  const { locale } = useCurrencyConfig();

  const { isHipaaCompliant } = useReturnsConfig();

  const { value: policyLabelText } = useLanguage({
    langKey: "returnsReviewPolicyCheckbox",
  });

  const { reviewPolicy } = useSettings();

  const { enableReviewPolicy, reviewPolicyText } = reviewPolicy || {};

  const policyLabel: string = reviewPolicyText || policyLabelText || "";

  const {
    mutate: setPudoOptionMutation,
    isLoading: loading,
    error: loadingError,
  } = useMutation({
    mutationFn: async () => {
      const data = await setPudoOption({
        client,
        isHipaaCompliant,
        selectedPudoOption,
        returnCart,
      });
      setReturnCart(data.setPudoOption);
    },
  });
  const setPudoOptionMutationRef = useRef(setPudoOptionMutation);

  useEffect(() => {
    setPudoOptionMutationRef.current();
  }, []);

  const submitReturnCardMutationEffect = isHipaaCompliant
    ? useHipaaSubmitReturnCartMutation
    : useSubmitReturnCartMutation;

  const [
    submitReturnCartMutation,
    { error: submitError, loading: submitting },
  ] = submitReturnCardMutationEffect({
    client,
    variables: {
      returnCartId: returnCart?.id || "",
      mailTo,
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (err: any) => {
      if (err) console.log(err);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onCompleted: ({ submitReturnCart: submittedReturn }: any) => {
      onSubmit?.({
        value: {
          submittedReturn,
        },
        area: "returnReview",
        event: "submit",
      });
    },
  });

  const handleBack = () => {
    onBack?.({
      value: {},
      area: "returnReview",
      event: "back",
    });
  };

  const handleSubmit = () => {
    void submitReturnCartMutation();
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange: CommonEvent<ReviewSummaryPayload> = (event: any) => {
    if (event.subComponent === "policyCheckbox") {
      setPolicyChecked(event?.value?.policyChecked || false);
    }
    if (event.subComponent === "mailToInput") {
      setMailTo(event?.value?.mailTo || "");
    }
  };

  const policy: {
    policyLabel: string;
    policyChecked: boolean;
  } | null =
    policyLabel && enableReviewPolicy
      ? {
          policyLabel,
          policyChecked,
        }
      : null;

  return (
    <ReviewSummaryRaw
      {...{
        locale,
        progressiveStep,
        activeStep,
        orderItems: orderData?.items || [],
        hasAssociatedEmail: orderData?.hasAssociatedEmail ?? true,
        selectedItems: selectedItems || [],
        fees: selectedPudoOption?.fees || [],
        dropOffLocation:
          returnCart?.pudoOption?.selectedDropOff?.dropOffLocation,
        error: loadingError || submitError,
        loading,
        allowRetry,
        mailTo,
        type: getPudoMethodName(selectedPudoOption),
        submitting,
        onBack: handleBack,
        onSubmit: handleSubmit,
        onChange: handleChange,
        returnCart,
        userRole,
        ...policy,
      }}
    />
  );
};

const ReviewSummary = withSettingsProvider(ReviewSummaryC, {
  settingsRequested: `
    reviewPolicy {
      reviewPolicyText
      enableReviewPolicy
    }
  `,
});

export { ReviewSummary, ReviewSummaryRaw };
